import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  DetailGrid,
  DetailPanel,
  SummaryRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const LOCATION_SEARCH_FIELDS = ['name', 'city', 'address'];
const LOCATION_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (l) => l.name?.toLowerCase() ?? '' },
];



const LocationComponent = () => {
  const emptyLocation = {
    name: '',
    seat_number: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    location_description: ''
  };
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(emptyLocation);
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleLocations,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(locations, {
    searchFields: LOCATION_SEARCH_FIELDS,
    sortOptions: LOCATION_SORT_OPTIONS,
  });

  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all locations
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    const response = await fetch(`${backendURL}/api/locations`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setLocations(data);
  };

  const handleFormChange = (e) => {
    setCurrentLocation({ ...currentLocation, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentLocation.location_id) {
      await updateLocation(currentLocation.location_id, currentLocation);
    } else {
      await createLocation(currentLocation);
    }
    setCurrentLocation(emptyLocation);
    setShowForm(false);
    fetchLocations();
  };

  const createLocation = async (locationData) => {
    await fetch(`${backendURL}/api/locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locationData),
      credentials: 'include'
    });
  };

  const updateLocation = async (locationId, locationData) => {
    await fetch(`${backendURL}/api/locations/${locationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locationData),
      credentials: 'include'
    });
  };

  const deleteLocation = async () => {
    if (locationToDelete) {
      await fetch(`${backendURL}/api/locations/${locationToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setLocationToDelete(null);
      setShowModal(false);
      fetchLocations();
    }
  };

  const editLocation = (location) => {
    setCurrentLocation(location);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentLocation(emptyLocation);
    setShowForm(false);
  }

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentLocation(emptyLocation);
    }
    setShowForm(!showForm);
  };

  const buildSubtitle = (location) => {
    const cityState = [location.city, location.state].filter(Boolean).join(', ');
    return cityState || 'No address on file';
  };

  const locationForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter location name"
            name="name"
            onChange={handleFormChange}
            value={currentLocation.name}
          />
        </Form.Group>
        <Form.Group controlId="seat_number">
          <Form.Label>Number of Seats</Form.Label>
          <Form.Control
            type="number"
            placeholder="Enter seat number"
            name="seat_number"
            onChange={handleFormChange}
            value={currentLocation.seat_number}
          />
        </Form.Group>
        <Form.Group controlId="address">
          <Form.Label>Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter address"
            name="address"
            onChange={handleFormChange}
            value={currentLocation.address}
          />
        </Form.Group>
        <Form.Group controlId="city">
          <Form.Label>City</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter city"
            name="city"
            onChange={handleFormChange}
            value={currentLocation.city}
          />
        </Form.Group>
        <Form.Group controlId="state">
          <Form.Label>State</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter state"
            name="state"
            onChange={handleFormChange}
            value={currentLocation.state}
          />
        </Form.Group>
        <Form.Group controlId="zip">
          <Form.Label>Zip</Form.Label>
          <Form.Control
            type="number"
            placeholder="Enter zip code"
            name="zip"
            onChange={handleFormChange}
            value={currentLocation.zip}
          />
        </Form.Group>
        <Form.Group controlId="location_description" className="crud-form-span-full">
          <Form.Label>Location Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter location description"
            name="location_description"
            onChange={handleFormChange}
            value={currentLocation.location_description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Location"
        listLabel="Show Locations List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Locations"
        form={locationForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="location"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search locations" placeholder="Name, city, or address" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No locations have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleLocations.map((location) => (
          <ExpandableRow
            key={location.location_id}
            isOpen={openId === location.location_id}
            onToggle={() => setOpenId(openId === location.location_id ? null : location.location_id)}
            main={{ title: location.name, subtitle: buildSubtitle(location) }}
            pills={location.seat_number ? [{ label: `${location.seat_number} seats` }] : []}
            actions={(
              <>
                <Button size="sm" onClick={() => editLocation(location)} aria-label="Edit location">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setLocationToDelete(location.location_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete location"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
            detail={(
              <DetailGrid columns={2}>
                <DetailPanel title="Venue">
                  <SummaryRow label="Address" value={location.address || 'None'} />
                  <SummaryRow label="City" value={location.city || 'None'} />
                  <SummaryRow label="State" value={location.state || 'None'} />
                  <SummaryRow label="Zip" value={location.zip || 'None'} />
                  <SummaryRow label="Seats" value={location.seat_number || 'Not set'} />
                </DetailPanel>
                <DetailPanel title="Description">
                  {location.location_description || 'No description provided.'}
                </DetailPanel>
              </DetailGrid>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteLocation}
        bodyText="Are you sure you want to delete this location?"
      />
    </div>
  );

};

export default LocationComponent;
